import { html, nothing } from "lit";
import type { LocalResourcesHistoryPoint, LocalResourcesSnapshot } from "../types.ts";
import { LOCAL_LLM_HINTS } from "../control-hints.ts";
import { fmtBytes } from "../local-llm/activity-lines.ts";
import { renderLocalLlmBottleneckStrip } from "../local-llm/bottleneck-strip.ts";
import {
  labelForLocalLlmModelStatus,
  resolveLocalLlmModelStatus,
} from "../local-llm/model-status.ts";
import { renderPerformanceFactorsTable } from "../local-llm/performance-factors.ts";

export type LocalLlmViewProps = {
  loading: boolean;
  error: string | null;
  snapshot: LocalResourcesSnapshot | null;
  consoleText: string;
  thinkingPreview: string | null;
  selectedModel: string | null;
  history: LocalResourcesHistoryPoint[];
  autoRefresh: boolean;
  includeGatewayLogs: boolean;
  connected: boolean;
  onRefresh: () => void;
  onClearConsole: () => void;
  onSelectModel: (model: string | null) => void;
  onAutoRefreshChange: (next: boolean) => void;
  onIncludeGatewayLogsChange: (next: boolean) => void;
};

function fmtTime(ms: number | null | undefined): string {
  if (!ms) {
    return "—";
  }
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

function isModelLoaded(snapshot: LocalResourcesSnapshot | null, name: string): boolean {
  if (!snapshot) {
    return false;
  }
  return snapshot.ollama.loadedModels.some((m) => m.name === name);
}

function isModelInUse(snapshot: LocalResourcesSnapshot | null, name: string): boolean {
  return resolveLocalLlmModelStatus(snapshot, name) === "in_use";
}

function runAgeSeconds(startedAtMs: number): number {
  return Math.max(0, Math.round((Date.now() - startedAtMs) / 1000));
}

export function renderLocalLlm(props: LocalLlmViewProps) {
  const snap = props.snapshot;
  const ollama = snap?.ollama;
  const installed = ollama?.installedModels ?? [];

  return html`
    <section class="card local-llm-page">
      <div class="row" style="justify-content: space-between; align-items: flex-start;">
        <div>
          <div class="card-title">Local LLM</div>
          <div class="card-sub">
            Read-only Ollama status on the gateway host. Activity lines come from
            <span class="mono">/api/tags</span> and <span class="mono">/api/ps</span>; log lines are
            filtered from gateway file logs (not Ollama daemon stdout). Install models with
            <span class="mono">ollama pull &lt;model&gt;</span> on the host. "In use" is inferred from
            active Ollama runs on the gateway (can be a different session than your current chat).
          </div>
        </div>
        <div class="row" style="gap: 8px; flex-wrap: wrap;">
          <button
            class="btn"
            ?disabled=${!props.connected || props.loading}
            title=${LOCAL_LLM_HINTS.refresh}
            @click=${props.onRefresh}
          >
            ${props.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      ${props.error ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>` : nothing}

      <div class="local-llm-status row" style="margin-top: 12px; gap: 16px; flex-wrap: wrap;">
        <div>
          <div class="llm-stats-muted">Ollama</div>
          <div class="local-llm-status__value">
            ${ollama?.reachable ? "Reachable" : "Unreachable"}
          </div>
        </div>
        <div>
          <div class="llm-stats-muted">Base URL</div>
          <div class="llm-stats-mono local-llm-status__value">${ollama?.baseUrl ?? "—"}</div>
        </div>
        <div>
          <div class="llm-stats-muted">Version</div>
          <div class="local-llm-status__value">${ollama?.version ?? "—"}</div>
        </div>
        <div>
          <div class="llm-stats-muted">Last refresh</div>
          <div class="local-llm-status__value">${fmtTime(snap?.collectedAtMs)}</div>
        </div>
      </div>

      <div class="row" style="margin-top: 16px; gap: 12px; flex-wrap: wrap; align-items: flex-end;">
        <label class="chat-composer-toolbar__field">
          <span class="chat-composer-toolbar__label">Installed Ollama model</span>
          <select
            class="chat-composer-toolbar__select"
            .value=${props.selectedModel ?? ""}
            ?disabled=${!props.connected}
            title=${LOCAL_LLM_HINTS.modelPicker}
            aria-label="Installed Ollama model"
            @change=${(e: Event) => {
              const value = (e.target as HTMLSelectElement).value;
              props.onSelectModel(value.trim() ? value : null);
            }}
          >
            <option value="" ?selected=${!props.selectedModel}>(overview — all models)</option>
            ${installed.map((model) => {
              const loaded = isModelLoaded(snap, model.name);
              const inUse = isModelInUse(snap, model.name);
              const suffix = inUse ? " · in use (ollama)" : loaded ? " · loaded" : "";
              return html`
                <option value=${model.name} ?selected=${props.selectedModel === model.name}>
                  ${model.name}${suffix}
                </option>
              `;
            })}
          </select>
        </label>

        <label class="chat-compose__send-option" title=${LOCAL_LLM_HINTS.autoRefresh}>
          <input
            type="checkbox"
            .checked=${props.autoRefresh}
            @change=${(e: Event) =>
              props.onAutoRefreshChange((e.target as HTMLInputElement).checked)}
          />
          <span>Auto-refresh (1s while inferencing, 10s idle)</span>
        </label>

        <label class="chat-compose__send-option" title=${LOCAL_LLM_HINTS.gatewayLogs}>
          <input
            type="checkbox"
            .checked=${props.includeGatewayLogs}
            @change=${(e: Event) =>
              props.onIncludeGatewayLogsChange((e.target as HTMLInputElement).checked)}
          />
          <span>Gateway logs (ollama)</span>
        </label>

        <button
          class="btn btn--sm"
          type="button"
          title=${LOCAL_LLM_HINTS.clearConsole}
          @click=${props.onClearConsole}
        >
          Clear console
        </button>
      </div>

      ${
        props.selectedModel && snap
          ? (() => {
              const status = resolveLocalLlmModelStatus(snap, props.selectedModel);
              const calloutClass =
                status === "in_use" ? "success" : status === "loaded" ? "success" : "";
              return html`
                <div class="callout ${calloutClass}" style="margin-top: 12px;">
                  <strong class="llm-stats-mono">${props.selectedModel}</strong>
                  ${status ? ` ${labelForLocalLlmModelStatus(status)}.` : ""}
                  ${
                    installed.find((m) => m.name === props.selectedModel)
                      ? html` Disk size ${fmtBytes(installed.find((m) => m.name === props.selectedModel)!.size)}.`
                      : nothing
                  }
                </div>
              `;
            })()
          : nothing
      }

      ${renderLocalLlmBottleneckStrip({ snapshot: snap, history: props.history })}

      ${renderPerformanceFactorsTable(snap, props.selectedModel)}

      ${
        snap
          ? html`
              <details class="local-llm-details" style="margin-top: 12px;">
                <summary class="llm-stats-muted">Live run details</summary>
                <div style="margin-top: 8px;">
                  <table class="llm-stats-network-table">
                    <thead>
                      <tr>
                        <th>Session</th>
                        <th>Model</th>
                        <th>Streaming</th>
                        <th>Age</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${snap.gateway.inflightRuns.map(
                        (run) => html`
                          <tr>
                            <td class="llm-stats-mono">${run.sessionId}</td>
                            <td class="llm-stats-mono">${run.modelId}</td>
                            <td>${run.streaming ? "yes" : "no"}</td>
                            <td class="llm-stats-mono">${runAgeSeconds(run.startedAtMs)}s</td>
                          </tr>
                        `,
                      )}
                      ${
                        snap.gateway.inflightRuns.length === 0
                          ? html`
                              <tr>
                                <td colspan="4" class="llm-stats-muted">No inflight runs.</td>
                              </tr>
                            `
                          : nothing
                      }
                    </tbody>
                  </table>
                </div>
              </details>
            `
          : nothing
      }

      ${
        props.thinkingPreview?.trim()
          ? html`
              <details class="local-llm-thinking-preview" style="margin-top: 12px;" open>
                <summary class="llm-stats-muted">Live thinking preview (Reasoning on/stream)</summary>
                <pre class="code-block mono" style="margin-top: 8px; max-height: 200px; overflow: auto;">${props.thinkingPreview}</pre>
              </details>
            `
          : nothing
      }

      <pre
        class="code-block dev-terminal local-llm-console"
        aria-readonly="true"
        aria-label="Local LLM activity console"
      >${props.consoleText || "Refresh to probe Ollama and append activity lines."}</pre>
    </section>
  `;
}
